import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface FormShellProps {
  submitting: boolean;
  onCancel: () => void;
  onReset?: () => void;
  children: React.ReactNode;
}

export function FormShell({ submitting, onCancel, onReset, children }: FormShellProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="grid gap-3">{children}</div>
      <div className="flex justify-end gap-2 border-t pt-3">
        {onReset ? (
          <Button variant="outline" onClick={onReset} disabled={submitting} className="mr-auto">
            {t('common.reset')}
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" form="integration-form" disabled={submitting} className="gap-1.5">
          {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
  required?: boolean;
}

export function Field({ label, htmlFor, hint, children, required }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

interface SecretInputProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export function SecretInput({ id, value, onChange, placeholder }: SecretInputProps): JSX.Element {
  const { t } = useTranslation();
  const [reveal, setReveal] = React.useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={reveal ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className="pr-9 font-mono text-xs"
      />
      <button
        type="button"
        onClick={() => setReveal((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        aria-label={reveal ? t('integrations.form.hideSecret') : t('integrations.form.showSecret')}
      >
        {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
